import { useDraggable } from '@dnd-kit/core';
import { GripVertical, Pin } from 'lucide-react';
import { partyDragId } from './dropTargets';

export interface PartyChipProps {
  guestId: string;
  /** Where the chip sits (a table uid, or "unseated") — two chips of one party never share an id. */
  where: string;
  name: string;
  /** What the chip says beside the name: "×4", "Seats 3–4", "2 of 4 seated". */
  detail?: string;
  side?: string | null;
  pinned?: boolean;
  selected?: boolean;
  /** Moved by a proposal being reviewed. */
  proposed?: boolean;
  draggable: boolean;
  onClick?: () => void;
}

/**
 * A party on the seating board — dragged onto a table or tapped to pick it
 * up. The party moves as one; its named people are placed from the party's
 * own sheet.
 */
export function PartyChip({
  guestId,
  where,
  name,
  detail,
  side,
  pinned = false,
  selected = false,
  proposed = false,
  draggable,
  onClick,
}: PartyChipProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: partyDragId(guestId, where),
    disabled: !draggable,
  });
  const tone = selected
    ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
    : proposed
      ? 'border-amber-300 bg-amber-50'
      : 'border-gray-200 bg-white hover:bg-gray-50';
  return (
    <div
      ref={setNodeRef}
      className={`flex items-center gap-1.5 rounded-lg border px-2 py-1.5 text-sm ${tone} ${
        isDragging ? 'opacity-40' : ''
      }`}
      data-testid={`party-${guestId}-${where}`}
    >
      {draggable && (
        <span
          {...attributes}
          {...listeners}
          aria-roledescription="Draggable party"
          aria-label={`Move ${name}`}
          className="touch-none cursor-grab text-gray-400 hover:text-gray-600"
        >
          <GripVertical size={14} />
        </span>
      )}
      <button
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        className="flex-1 min-w-0 flex items-center gap-1.5 text-left"
      >
        <span className="truncate font-medium text-gray-900">{name}</span>
        {detail && <span className="shrink-0 text-xs text-gray-500">{detail}</span>}
        {side && (
          <span className="shrink-0 rounded-full bg-gray-100 px-1.5 text-[11px] text-gray-600">
            {side}
          </span>
        )}
        {pinned && <Pin size={12} className="shrink-0 text-gray-500" aria-label="Pinned" />}
      </button>
    </div>
  );
}
